import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from 'react'
import { useLocation } from 'react-router-dom'
import { checkActiveWindow, getRevokeAccessModuleFromUrl } from '../Helpers/RevokeAccess'

interface ActiveWindowContextType {
  message: string
  actions: string[]
  hasActiveWindow: boolean
  isActionBlocked: (actionCode: string) => boolean
  refresh: () => void
}

const ActiveWindowContext = createContext<ActiveWindowContextType | null>(null)

interface ActiveWindowProviderProps {
  children: ReactNode
  // The Revoke Access module identifier. When empty, no check is performed
  // and nothing is blocked (safe no-op for routes without a module).
  module?: string
}

export const ActiveWindowProvider: React.FC<ActiveWindowProviderProps> = ({
  children,
  module = '',
}) => {
  const [message, setMessage] = useState<string>('')
  const [actions, setActions] = useState<string[]>([])

  const fetchActiveWindow = useCallback(async () => {
    if (!module) {
      setMessage('')
      setActions([])
      return
    }
    const { message: activeMessage, actions: activeActions } = await checkActiveWindow(module)
    setMessage(activeMessage)
    setActions(activeActions)
  }, [module])

  useEffect(() => {
    fetchActiveWindow()
  }, [fetchActiveWindow])

  const hasActiveWindow = message.length > 0

  const isActionBlocked = useCallback(
    (actionCode: string) => hasActiveWindow && actions.includes(actionCode),
    [hasActiveWindow, actions],
  )

  const contextValue = useMemo(
    () => ({
      message,
      actions,
      hasActiveWindow,
      isActionBlocked,
      refresh: fetchActiveWindow,
    }),
    [message, actions, hasActiveWindow, isActionBlocked, fetchActiveWindow],
  )

  return (
    <ActiveWindowContext.Provider value={contextValue}>{children}</ActiveWindowContext.Provider>
  )
}

// Route-aware wrapper: resolves the current module from the URL and provides
// active-window state to the whole subtree. Mount once around the app shell.
// Re-resolving on navigation is cheap; unknown routes yield an empty module
// (no API call, nothing blocked).
export const RouteActiveWindowProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation()
  const module = getRevokeAccessModuleFromUrl(location.pathname)
  return <ActiveWindowProvider module={module}>{children}</ActiveWindowProvider>
}

// Access the full active-window context. Returns a safe no-op default when
// used outside a provider so guarded CTAs never crash.
export const useActiveWindow = (): ActiveWindowContextType => {
  const context = useContext(ActiveWindowContext)
  if (!context) {
    return {
      message: '',
      actions: [],
      hasActiveWindow: false,
      isActionBlocked: () => false,
      refresh: () => undefined,
    }
  }
  return context
}

// Convenience hook for a single CTA. Returns whether it should be disabled
// and the message to surface. Pass the stable action code the CTA is tagged
// with (see REVOKE_ACCESS_ACTIONS).
export const useActionGuard = (actionCode: string): { isBlocked: boolean; message: string } => {
  const { isActionBlocked, message } = useActiveWindow()
  return { isBlocked: isActionBlocked(actionCode), message }
}
